import "@elastic/react-search-ui-views/lib/styles/styles.css";
import styles from "../../assets/scss/modules/PublicListings.module.scss";
import React, {ComponentType, Fragment, HTMLAttributes, useEffect, useState} from 'react';
import {
    CtrInscriptions,
    PropertyHit,
    Query,
    QuerySearchPropertiesArgs,
    SearchableListing
} from "../../graphql/generated/graphql-main.tsx";
import clsx from "clsx";
import {IconButton, InputAdornment, ListItemButton, ListItemText} from "@mui/material";
import {XTextField} from "../global/XTextField.tsx";
import {useWpContext} from "../context/WordPressProvider.tsx";
import {Clear, Search} from "@mui/icons-material";
import {apollo11, gqlTemplates} from "../../utils/gql-client.ts";
import {nameof, strEmpty} from "../../utils/utils.ts";
import {SearchBox, SearchProvider, WithSearch} from "@elastic/react-search-ui";
import AppSearchAPIConnector from "@elastic/search-ui-app-search-connector";
import {SearchDriverOptions} from "@elastic/search-ui";
import {InputViewProps, SearchBoxAutocompleteViewProps} from "@elastic/react-search-ui-views";
import {cfg} from "../../config.ts";
import useReactTexts from "../../hooks/useReactTexts.ts";
import useSiteConfig from "../../hooks/useSiteConfig.ts";

interface Props extends HTMLAttributes<HTMLDivElement> {
    // whether to use search bar in standalone mode independently of Public.Listings
    standalone?: boolean;
    // array of listings from main query
    listings: CtrInscriptions[];
    // what to do when search module returns either hits (direct search) or the term (in standalone mode)
    onSearch: (term: string, hits: PropertyHit[] | null) => void;
}

// available from Enterprise Search > App Search > Credentials
// todo move to @elastic/search-ui-elasticsearch-connector & stop using App Search (deprecated)
const connector = new AppSearchAPIConnector({
    searchKey: cfg.elastic.api_key,
    engineName: cfg.elastic.engine,
    endpointBase: cfg.elastic.endpoint
});

// which fields to search in when doing autocomplete
const autocompleteFields: Array<keyof SearchableListing> =
    ["mls_id", "address_fr", "title_fr", "title_en", "unit_search"];

// note: this module works for both search & autocomplete, but we use ONLY autocomplete
const config: SearchDriverOptions = {
    // disable search on load to prevent ?size=n_20_n from appearing in URL on load (after search it's ok, though)
    alwaysSearchOnInitialLoad: false,
    apiConnector: connector,
    hasA11yNotifications: true,
    // disable 'search' (?size=n_20_n) feature since we only use 'autocomplete' and do search separately via our API
    onSearch: (query, queryConfig, next) => {
        return new Promise(() => null);
    },
    autocompleteQuery: {
        suggestions: {
            types: {
                documents: {fields: autocompleteFields}
            }
        }
    },
};

/**
 * Main 'advanced' search mechanism using autocomplete from Elastic's SearchUI library
 * @param props
 * @constructor
 */
const FiltersSearchAdvanced = (props: Props) => {
    const {standalone, onSearch} = props;
    const {others, is_fr} = useWpContext<ZListings>();
    const {__} = useReactTexts();
    const {$config} = useSiteConfig();

    // useful if we want to display a loading cue when searching ...
    const [searching, setSearching] = useState<boolean>(false);

    // MLS IDs that can be searched through (used to restrict autocomplete to listings relevant per site)
    const validMlsIds: string[] = props.listings.map(it => it.NO_INSCRIPTION).filter(it => it != null);

    const updateQueryString = (term?: string | null) => {
        // update url query string w/ searched term (useful when sharing link with others)
        // https://developer.mozilla.org/en-US/docs/Web/API/History/pushState
        const _url = new URL(window.location.href);
        if (strEmpty(term)) _url.searchParams.delete(cfg.react.search.url_key);
        else _url.searchParams.set(cfg.react.search.url_key, term ?? "");
        window.history.pushState(null, "", _url);
    }

    // launch real search query to ElasticSearch to produce final results; autocomplete uses different type of query
    const searchIt = async (directTerm?: string) => {
        const _term = directTerm;
        updateQueryString(_term);

        // empty string == resetting field, so return null to calling component
        if (strEmpty(_term)) {
            onSearch("", null);
            return;
        }

        // in standalone mode, do NOT perform actual search, only send to public listings page
        if (standalone) {
            onSearch(_term!, null);
            return;
        }

        // launch search via GraphQL through Elastic
        setSearching(true);
        const {data} = await apollo11.query<Query, QuerySearchPropertiesArgs>({
            query: gqlTemplates.searchProperties, variables: {term: _term}
        });
        setSearching(false);

        // return the search hits from Elastic
        const _hits = (data?.searchProperties ?? []) as PropertyHit[];
        console.info(`hits`, _hits);
        onSearch(_term!, _hits);
    }

    /**
     * customize SearchUI's input component
     * @param setSearchTerm
     * @source
     * - https://www.elastic.co/docs/current/search-ui/api/react/components/search-box#input-view-customization
     */
    const customInputView = (setSearchTerm?: Function) => {
        return function (props: InputViewProps) {
            const {getInputProps, getButtonProps, getAutocomplete} = props;
            // only way of clearing input when using Elastic's official React SearchUI module
            const _clearSearch = () => {
                setSearchTerm?.("");
                updateQueryString("");
                onSearch("", null);
            }

            return <Fragment>
                <div className={clsx("sui-search-box__wrapper", styles.es_search_input)}>
                    {/* actual search input box */}
                    <XTextField
                        // className={clsx("w-full lg:w-1/2 self-center", styles.dd_autocomplete, styles.search_bar)}
                        className={clsx("", styles.dd_autocomplete, styles.search_bar)}
                        slotProps={{
                            htmlInput: {
                                ...getInputProps(),
                                className: "",
                                placeholder: __("Région, Adresse, Code postal ou Numéro d'inscription")
                            },
                            input: {
                                endAdornment: <InputAdornment position="end" className={"space-x-2"}>
                                    <IconButton edge={"end"} size={"small"} {...getButtonProps()} className={""}>
                                        <Search/>
                                    </IconButton>
                                    {/* hide clear icon in standalone mode to prevent unexpected redirect */}
                                    {!standalone && <IconButton edge={"end"} size={"small"} onClick={_clearSearch}>
                                        <Clear/>
                                    </IconButton>}
                                </InputAdornment>
                            },
                            inputLabel: {
                                shrink: false
                            }
                        }}
                    ></XTextField>
                    {/* show linear progress when searching */}
                    {/*{searching && <LinearProgress className={""}/>}*/}
                    {/*{searching && <LinearProgress className={"w-full lg:w-1/2 self-center"}/>}*/}
                </div>
                {getAutocomplete()}
            </Fragment>
        } as ComponentType<InputViewProps>;
    }

    /**
     * customize SearchUI's autocomplete view
     * @param props
     * @source
     * - https://www.elastic.co/docs/current/search-ui/api/react/components/search-box#autocomplete-view-customization
     */
    const customAutocompleteView: ComponentType<SearchBoxAutocompleteViewProps> = (props) => {
        const {autocompletedResults, getItemProps} = props;
        console.info(`autocomplete-results`, autocompletedResults);

        // note: filter suggestions to only include listings for this site (restrictions on brokers, offices, etc.)
        return <ul className={clsx("sui-search-box__autocomplete-container", styles.es_search_autocomplete)}>
            {autocompletedResults
                .filter(it => validMlsIds.includes(it._meta.id))
                .map((result, i) => {
                    const mlsId = result._meta.id; // since we're using 'NO_INSCRIPTION' as id in hub for indexing
                    return <ListItemButton
                        {...getItemProps({key: mlsId, index: i, item: result})}
                        key={mlsId}
                        className={""} // remove default class on li to fully customize it to our liking
                        component={"a"}
                        target={$config.listing_target_self ? "_self" : "_blank"}
                        href={`${others.url_listing_single}${mlsId ?? ""}/`}
                    >
                        <ListItemText
                            primary={result.address_fr.raw}
                            secondary={`#${result._meta.id}`}
                        ></ListItemText>
                    </ListItemButton>
                })}
        </ul>
    }

    /**
     * If search term is provided via URL query (from standalone search), run auto-search immediately.
     * - https://upstackhq.com/blog/software-development/how-to-get-a-parameter-from-a-url-in-react
     * Note: auto-search should ONLY apply on Public.Listings, and should NEVER run on same page (Elementor)
     *  where standalone search widget is set; otherwise, this causes automatic popup opens on home page
     *  since it listens to its own URL which contains the ?q=<term> and causes it to do auto-search on home page.
     * Note: parsing of location.search MUST ALWAYS be done inside useEffect to properly handle switching
     *  between GRID & MAPS view. That's the only reliable way to fetch up-to-date URL search query. Previously,
     *  I was fetching this outside the React component, but it caused different issues w/ out-of-sync URL query.
     */
    useEffect(() => {
        // fetch search query from URL (applies if coming from standalone search)
        const urlSearch = new URLSearchParams(window.location.search).get(cfg.react.search.url_key);
        console.info(`url-search-term -> ${urlSearch}`);

        // if standalone, do NOT perform auto-search since redundant
        if (strEmpty(urlSearch) || standalone) return;
        searchIt(urlSearch!);
    }, []);

    return <SearchProvider config={config}>
        <WithSearch mapContextToProps={({setSearchTerm}) => ({setSearchTerm})}>
            {({setSearchTerm}) => (
                <SearchBox
                    className={"searchbar"}
                    searchAsYouType={true}
                    autocompleteSuggestions={true}
                    autocompleteResults={{
                        titleField: nameof<SearchableListing>(is_fr ? "address_fr" : "address_en"),
                        urlField: ""
                    }}
                    // submit = click on loop icon OR press 'Enter' inside search box
                    onSubmit={searchTerm => {
                        console.info(`search-term`, searchTerm);
                        searchIt(searchTerm);
                    }}
                    // note: the redirection is made via <ListItemButton>, not via JS
                    onSelectAutocomplete={(e: any) => {
                        console.info(`select-suggestion`, e);
                        // todo optionally clear search term when user only clicks a suggestion rather than searching
                        // setSearchTerm("");
                    }}
                    inputView={customInputView(setSearchTerm)}
                    autocompleteView={customAutocompleteView}
                ></SearchBox>
            )}
        </WithSearch>
    </SearchProvider>
}

export default FiltersSearchAdvanced;
